import { inject, onBeforeUnmount, type Ref } from "vue";
import { UPLOAD_PORT } from "@/shared/contracts/ports";
import { documentVersions, readDocument } from "../api/document-content";
import {
	applyDocumentEdits,
	type ChatDocument,
	type DocumentEdits,
	documentEdits,
	documentKind,
} from "./chat-document";

export function useLiveEdits(document: Ref<ChatDocument | null>) {
	const uploads = inject(UPLOAD_PORT, null);
	let controller: AbortController | null = null;
	let pending: DocumentEdits | null = null;
	let base: string | null = null;
	let callId: string | undefined;
	function reset(): void {
		controller?.abort();
		controller = null;
		pending = null;
		base = null;
		callId = undefined;
	}
	onBeforeUnmount(reset);
	function paint(): void {
		if (!pending || base === null) return;
		const content = applyDocumentEdits(base, pending.edits);
		const kind = documentKind(pending.path);
		if (content !== null && kind)
			document.value = { name: pending.path, content, kind, status: "writing", callId };
	}
	async function update(tool: string, input: unknown, id?: string): Promise<void> {
		const edit = documentEdits(tool, input);
		if (!edit || !uploads) return;
		if (pending?.path !== edit.path) reset();
		pending = edit;
		callId = id;
		if (controller) {
			paint();
			return;
		}
		const current = new AbortController();
		controller = current;
		try {
			const versions = await documentVersions(edit.path, current.signal);
			const file = versions[0];
			if (!file || current.signal.aborted) return;
			const content = await readDocument(uploads, file.id, current.signal);
			if (current.signal.aborted) return;
			base = content;
			paint();
		} catch {
			/* The writing activity remains available when a preview cannot be read. */
		}
	}
	return { update, reset };
}
